import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ContactGroupSummary,
  ContactImportCapacityPreview,
  ContactImportColumnMapping,
} from "@multi-chat/shared";
import { AppNotice } from "../../../components/common/AppNotice.js";
import { ChannelIcon } from "../../../components/channel/ChannelIcon.js";
import { Button } from "../../../components/ui/button/index.js";
import { createContactGroup } from "../../../api/contacts-api.js";
import { mapContactError, readApplicationErrorCode } from "../contact-errors.js";
import { ContactGroupMultiSelect } from "../ContactGroupMultiSelect/index.js";
import type { ContactGroupSelectionValue } from "../contact-group-selection-value.js";
import { ImportContactsOutcomePanel } from "./ImportContactsOutcomePanel.js";

function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/[A-Z]/g, (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)));
}

export function ImportContactsReviewStep({
  headers,
  mapping,
  phoneUnmapped,
  capacity,
  capacityLoading,
  autoLink,
  summaryAutoLink,
  rowCount,
  countryIso,
  countryLabel,
  groups,
  applyGroups,
  onChangeApplyGroups,
  onGroupsChanged,
  organizationId,
  onEditMapping,
  disabled,
}: {
  headers: string[];
  mapping: ContactImportColumnMapping;
  phoneUnmapped: boolean;
  capacity: ContactImportCapacityPreview | null;
  capacityLoading: boolean;
  autoLink: boolean;
  summaryAutoLink: boolean;
  rowCount: number;
  countryIso: string;
  countryLabel: string;
  groups: ContactGroupSummary[];
  applyGroups: ContactGroupSelectionValue;
  onChangeApplyGroups: (next: ContactGroupSelectionValue) => void;
  onGroupsChanged?: () => void | Promise<void>;
  organizationId: number;
  onEditMapping: () => void;
  disabled?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const mapped = mapping.flatMap((entry, index) =>
    entry.target === "skip"
      ? []
      : [
          {
            source: headers[index] ?? entry.source,
            target: t(`contacts.import.mapping.targets.${entry.target}`),
          },
        ],
  );

  return (
    <div className="grid gap-4">
      <ImportContactsOutcomePanel
        capacity={capacity}
        capacityLoading={capacityLoading}
        autoLink={summaryAutoLink}
        rowCount={rowCount}
      />
      <div className="grid gap-1.5">
        <p className="m-0 text-[0.8125rem] font-medium text-foreground">
          {t("contacts.import.settingsTitle")}
        </p>
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          <li className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-surface-muted px-2 py-0.5 text-[0.75rem]">
            <span aria-hidden="true">{flagEmoji(countryIso)}</span>
            <span className="truncate">{countryLabel}</span>
          </li>
          <li className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-surface-muted px-2 py-0.5 text-[0.75rem]">
            <ChannelIcon platformType="WHATSAPP" size={12} className="text-whatsapp" />
            <span>
              {autoLink
                ? t("contacts.import.review.whatsappOn")
                : t("contacts.import.review.whatsappOff")}
            </span>
          </li>
        </ul>
      </div>
      <div className="grid gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="m-0 text-[0.8125rem] font-medium text-foreground">
            {t("contacts.import.review.mappingTitle")}
          </p>
          <button
            type="button"
            className="min-h-8 cursor-pointer text-[0.8125rem] font-medium text-accent"
            data-testid="contact-import-edit-mapping"
            disabled={disabled}
            onClick={onEditMapping}
          >
            {t("contacts.import.capacity.editMapping")}
          </button>
        </div>
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {mapped.map((entry) => (
            <li
              key={entry.source}
              className="inline-flex max-w-full items-center gap-1 rounded-md bg-surface-muted px-2 py-0.5 text-[0.75rem]"
            >
              <span className="truncate text-muted">{entry.source}</span>
              <span aria-hidden="true" className="shrink-0 text-muted rtl:-scale-x-100">
                →
              </span>
              <span className="shrink-0">{entry.target}</span>
            </li>
          ))}
        </ul>
        {phoneUnmapped ? (
          <p className="m-0 text-[0.75rem] text-muted">
            {t("contacts.import.capacity.phoneUnmappedTitle")}
          </p>
        ) : null}
      </div>
      <ImportReviewGroups
        groups={groups}
        applyGroups={applyGroups}
        onChangeApplyGroups={onChangeApplyGroups}
        onGroupsChanged={onGroupsChanged}
        organizationId={organizationId}
        disabled={disabled}
      />
      <AppNotice
        severity="info"
        className="px-3 py-2"
        description={t("contacts.import.review.whatHappens")}
        testId="contact-import-capacity-note"
      />
    </div>
  );
}

function ImportReviewGroups({
  groups,
  applyGroups,
  onChangeApplyGroups,
  onGroupsChanged,
  organizationId,
  disabled,
}: {
  groups: ContactGroupSummary[];
  applyGroups: ContactGroupSelectionValue;
  onChangeApplyGroups: (next: ContactGroupSelectionValue) => void;
  onGroupsChanged?: () => void | Promise<void>;
  organizationId: number;
  disabled?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const hasGroups = groups.length > 0;

  async function handleCreate(): Promise<void> {
    const name = draft.trim();
    if (name.length === 0 || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createContactGroup(organizationId, name);
      onChangeApplyGroups({
        ...applyGroups,
        groupIds: applyGroups.groupIds.includes(created.id)
          ? applyGroups.groupIds
          : [...applyGroups.groupIds, created.id],
      });
      await onGroupsChanged?.();
      setDraft("");
      setCreateOpen(false);
    } catch (error) {
      const code = readApplicationErrorCode(error);
      setCreateError(
        code === "CONFLICT"
          ? t("contacts.import.applyGroups.duplicateName")
          : mapContactError(t, error, t("contacts.import.applyGroups.createError")),
      );
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="grid justify-items-start gap-1.5" data-testid="contact-import-groups">
      <p className="m-0 flex items-baseline gap-1.5 text-[0.8125rem]">
        <span className="font-medium text-foreground">{t("contacts.import.applyGroups.title")}</span>
        <span className="text-[0.75rem] text-muted">{t("contacts.import.applyGroups.optional")}</span>
      </p>
      <p className="m-0 text-[0.75rem] leading-snug text-muted">
        {t("contacts.import.applyGroups.help")}
      </p>
      {hasGroups ? (
        <ContactGroupMultiSelect
          groups={groups}
          value={applyGroups}
          onChange={onChangeApplyGroups}
          disabled={disabled}
          allowCreateNewGroup={false}
          showEmptyHint={false}
          presentation="section"
          density="review"
          triggerClassName="w-fit max-w-56 justify-self-start"
          menuClassName="w-56"
        />
      ) : (
        <p className="m-0 text-[0.75rem] text-muted">{t("contacts.import.applyGroups.none")}</p>
      )}
      {createOpen ? (
        <form
          className="grid w-full max-w-56 gap-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            void handleCreate();
          }}
        >
          <input
            type="text"
            value={draft}
            maxLength={120}
            disabled={disabled || creating}
            aria-label={t("contacts.groups.multiSelect.createPlaceholder")}
            placeholder={t("contacts.groups.multiSelect.createPlaceholder")}
            data-testid="contact-import-new-group-name"
            className="h-8 rounded-md border border-border bg-background px-2 text-[0.75rem]"
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              variant="outline"
              size="sm"
              className="h-8 px-2 text-[0.75rem]"
              disabled={disabled || creating || draft.trim().length === 0}
              data-testid="contact-import-create-group"
            >
              {t("contacts.groups.multiSelect.createAction")}
            </Button>
          </div>
          {createError ? (
            <p className="m-0 text-[0.75rem] text-destructive" role="alert">
              {createError}
            </p>
          ) : null}
        </form>
      ) : (
        <button
          type="button"
          className="min-h-8 cursor-pointer text-[0.75rem] font-medium text-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-70"
          data-testid="contact-import-new-group"
          disabled={disabled}
          onClick={() => setCreateOpen(true)}
        >
          {t("contacts.import.applyGroups.newGroup")}
        </button>
      )}
    </div>
  );
}
